import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowUp, ArrowDown, Minus, TrendingUp,
  Radar, Sparkles, FileText, Users, RefreshCw, ExternalLink,
  Loader2, ChevronRight, Shield, Eye, Newspaper, Cpu, Download
} from "lucide-react";
import { OFFICERS, OfficersGrid, ActiveOfficerBanner } from "@/components/AIOfficers";
import PoliticalDossier from "@/components/PoliticalDossier";
import OfficerChat from "@/components/OfficerChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const FALLBACK_PROFILES = [
  { id: "dania-gonzalez-sv", name: "Dania González", role: "Diputada por San Salvador", party: "Nuevas Ideas", country: "El Salvador" },
  { id: "dennis-salinas-sv", name: "Dennis Salinas", role: "Diputado", party: "Nuevas Ideas", country: "El Salvador" },
];

const VIEW_TO_OFFICER = {
  overview: "analyst",
  brief: "executive",
  stakeholders: "stakeholder",
  forecast: "forecast",
};

const mapViewToOfficer = (view) => VIEW_TO_OFFICER[view] || "analyst";
const officerToView = (officerId) => Object.entries(VIEW_TO_OFFICER).find(([, oid]) => oid === officerId)?.[0] || "overview";

const PoliticalIntel = () => {
  const [profiles, setProfiles] = useState(FALLBACK_PROFILES);
  const [selectedId, setSelectedId] = useState(FALLBACK_PROFILES[0].id);

  // Expose active profile to FloatingOfficerDock so its context resolver finds the right id
  useEffect(() => {
    try { localStorage.setItem("proxima_active_profile_id", selectedId); } catch (e) {}
    return () => {
      try { localStorage.removeItem("proxima_active_profile_id"); } catch (e) {}
    };
  }, [selectedId]);
  const [intel, setIntel] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [stakeholders, setStakeholders] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [chatOfficerId, setChatOfficerId] = useState(null);

  const exportDossierPdf = async () => {
    if (!selectedId) return;
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/political/intel/${selectedId}/dossier/pdf`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `proxima-dossier-${selectedId}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier exportado");
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("Error al exportar PDF");
    }
    setExportingPdf(false);
  };

  // Try to load fresh profiles from API; keep fallbacks if it fails
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/political/profiles`).then(res => {
      if (!mounted) return;
      if (res.data?.profiles?.length) setProfiles(res.data.profiles);
    }).catch((e) => {
      console.warn("[PoliticalIntel] using fallback profiles:", e?.message);
    });
    return () => { mounted = false; };
  }, []);

  const loadIntel = useCallback(async (id) => {
    const useId = id || selectedId;
    if (!useId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/political/intel/${useId}`, { timeout: 90000 });
      setIntel(res.data);
    } catch (e) {
      console.error("loadIntel failed:", e?.response?.status, e?.message);
      toast.error("Error cargando intel - intenta refrescar");
    }
    setLoading(false);
  }, [selectedId]);

  const refreshIntel = async () => {
    if (!selectedId) return;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/political/intel/${selectedId}/refresh`, {}, { timeout: 120000 });
      setIntel(res.data);
      toast.success(`${res.data.total_mentions} menciones actualizadas`);
    } catch (e) { toast.error("Error al refrescar"); }
    setRefreshing(false);
  };

  const loadStakeholders = async () => {
    if (!selectedId) return;
    try {
      const res = await axios.post(`${API}/political/intel/${selectedId}/stakeholders`, {}, { timeout: 60000 });
      setStakeholders(res.data);
    } catch (e) { toast.error("Error en stakeholders"); }
  };

  const loadForecast = async () => {
    if (!selectedId) return;
    try {
      const res = await axios.post(`${API}/political/intel/${selectedId}/forecast`, {}, { timeout: 30000 });
      setForecast(res.data);
    } catch (e) { toast.error("Error en forecast"); }
  };

  const generateBrief = async () => {
    if (!selectedId) return;
    setBrief("LOADING");
    try {
      const res = await axios.post(`${API}/political/intel/${selectedId}/brief`, {}, { timeout: 90000 });
      setBrief(res.data.brief);
    } catch (e) {
      toast.error("Error generando brief");
      setBrief("");
    }
  };

  useEffect(() => {
    if (selectedId) {
      loadIntel(selectedId);
      setStakeholders(null);
      setForecast(null);
      setBrief("");
      // Load dossier in parallel (silent — optional curated data)
      axios.get(`${API}/political/intel/${selectedId}/dossier`).then(res => {
        setDossier(res.data);
      }).catch(() => {
        setDossier(null);
      });
    }
  }, [selectedId, loadIntel]);

  const currentProfile = profiles.find(p => p.id === selectedId);

  return (
    <div className="min-h-screen bg-[#030014] pt-28 pb-20 px-4 sm:px-6 lg:px-8" data-testid="political-intel-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-purple-400 font-medium">PROXIMA Command Center · Political Intelligence</span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 tracking-tight">Sala de Crisis Reputacional</h1>
          <p className="text-sm text-gray-400 mt-2">6 AI Officers especializados · Datos reales de medios LATAM · Procesado con IA en vivo</p>
        </div>

        {/* AI Officers grid */}
        <OfficersGrid activeId={mapViewToOfficer(activeView)} onSelect={(id) => {
          const view = officerToView(id);
          setActiveView(view);
          if (view === "stakeholders" && !stakeholders) loadStakeholders();
          if (view === "forecast" && !forecast) loadForecast();
          if (view === "brief" && !brief) generateBrief();
          // Open interactive chat with selected officer
          setChatOfficerId(id);
        }} />

        {/* Profile selector */}
        <div className="flex flex-wrap gap-3 mb-6">
          {profiles.map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              className={`px-5 py-3 rounded-2xl border transition-all ${
                selectedId === p.id
                  ? "bg-purple-500/15 border-purple-500/40 text-white"
                  : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.04]"
              }`}
              data-testid={`profile-${p.id}`}
            >
              <p className="text-sm font-medium">{p.name}</p>
              <p className="text-[10px] uppercase tracking-widest opacity-60">{p.role} · {p.party}</p>
            </button>
          ))}
          <button
            onClick={refreshIntel}
            disabled={refreshing}
            className="ml-auto px-4 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-intel-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? "Refrescando..." : "Refrescar Intel"}
          </button>
          <button
            onClick={exportDossierPdf}
            disabled={exportingPdf || !intel}
            className="px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.08] text-white text-xs font-medium hover:bg-white/[0.07] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="export-dossier-pdf-btn"
            title="Exportar dossier como PDF"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exportingPdf ? "Generando..." : "Exportar PDF"}
          </button>
        </div>

        {loading && (
          <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-12 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            <p className="text-sm text-gray-400">Cargando inteligencia en vivo...</p>
          </div>
        )}

        {!loading && intel && currentProfile && (
          <>
            {/* IRI overview row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <IRICard intel={intel} profile={currentProfile} />
              <SentimentCard intel={intel} />
              <RiskCard intel={intel} />
            </div>

            {/* Curated Dossier (real bio + X posts + reputational risk events) */}
            {dossier && <PoliticalDossier dossier={dossier} />}

            {/* View tabs - hidden when officer is selected, but kept for navigation */}
            <ActiveOfficerBanner officerId={mapViewToOfficer(activeView)} />

            <AnimatePresence mode="wait">
              <motion.div key={activeView} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                {activeView === "overview" && <MentionsTab intel={intel} />}
                {activeView === "brief" && <BriefTab brief={brief} onRegenerate={generateBrief} />}
                {activeView === "stakeholders" && <StakeholdersTab data={stakeholders} onLoad={loadStakeholders} />}
                {activeView === "forecast" && <ForecastTab data={forecast} onLoad={loadForecast} />}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Interactive AI Officer chat panel */}
      <OfficerChat
        officerId={chatOfficerId}
        profileId={selectedId}
        profileName={currentProfile?.name}
        open={!!chatOfficerId}
        onClose={() => setChatOfficerId(null)}
      />
    </div>
  );
};

// ============ COMPONENT BLOCKS ============

const IRICard = ({ intel, profile }) => {
  const grade = intel.grade;
  const gradeColor = grade === "A" ? "text-emerald-400" : grade === "B" ? "text-cyan-400" : grade === "C" ? "text-yellow-400" : "text-red-400";
  const dash = (intel.iri_score / 100) * 264;
  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6" data-testid="iri-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">IRI Score</span>
        <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-medium">en vivo</span>
      </div>
      <div className="flex items-center gap-5">
        <div className="relative w-28 h-28">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.06)" strokeWidth="7" fill="none" />
            <motion.circle cx="50" cy="50" r="42" stroke="url(#iriGrad)" strokeWidth="7" fill="none" strokeLinecap="round"
              initial={{ strokeDasharray: "0 264" }} animate={{ strokeDasharray: `${dash} 264` }} transition={{ duration: 1.5, ease: "easeOut" }} />
            <defs><linearGradient id="iriGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#a855f7" /><stop offset="1" stopColor="#ec4899" /></linearGradient></defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white">{intel.iri_score}</span>
            <span className={`text-xs font-bold ${gradeColor}`}>Grade {grade}</span>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-base font-semibold text-white leading-tight">{profile.name}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">{profile.role}</p>
          <p className="text-[10px] text-purple-300 mt-2">{intel.total_mentions} menciones · {profile.party}</p>
        </div>
      </div>
    </div>
  );
};

const SentimentCard = ({ intel }) => {
  const { positive = 0, negative = 0, neutral = 0 } = intel.sentiment_breakdown || {};
  const total = positive + negative + neutral || 1;
  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6" data-testid="sentiment-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Sentimiento</span>
        <Activity className="w-3.5 h-3.5 text-gray-500" />
      </div>
      <div className="space-y-3">
        <SentimentBar label="Positivo" value={positive} total={total} color="from-emerald-500 to-emerald-400" />
        <SentimentBar label="Negativo" value={negative} total={total} color="from-red-500 to-red-400" />
        <SentimentBar label="Neutral" value={neutral} total={total} color="from-gray-500 to-gray-400" />
      </div>
    </div>
  );
};

const SentimentBar = ({ label, value, total, color }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-gray-400">{label}</span>
      <span className="text-white font-mono">{value} ({Math.round(value / total * 100)}%)</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
      <motion.div initial={{ width: 0 }} animate={{ width: `${value / total * 100}%` }} transition={{ duration: 0.8 }}
        className={`h-full bg-gradient-to-r ${color}`} />
    </div>
  </div>
);

const RiskCard = ({ intel }) => {
  const { high = 0, medium = 0, low = 0 } = intel.risk_breakdown || {};
  const total = high + medium + low || 1;
  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6" data-testid="risk-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Riesgo</span>
        <AlertTriangle className={`w-3.5 h-3.5 ${high > 0 ? "text-red-400" : "text-gray-500"}`} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Alto", value: high, color: "bg-red-500/15 border-red-500/30 text-red-300" },
          { label: "Medio", value: medium, color: "bg-yellow-500/15 border-yellow-500/30 text-yellow-300" },
          { label: "Bajo", value: low, color: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" },
        ].map(r => (
          <div key={r.label} className={`p-3 rounded-xl border ${r.color} text-center`}>
            <p className="text-2xl font-bold">{r.value}</p>
            <p className="text-[10px] uppercase tracking-widest opacity-80">{r.label}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 mt-3">Top fuentes: {intel.top_sources?.slice(0, 2).map(([s, n]) => `${s} (${n})`).join(" · ")}</p>
    </div>
  );
};

const MentionsTab = ({ intel }) => (
  <div className="space-y-2" data-testid="mentions-list">
    <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">{intel.mentions?.length || 0} menciones reales · procesadas con Claude</p>
    {intel.mentions?.map((m, i) => <MentionCard key={i} m={m} />)}
  </div>
);

const MentionCard = ({ m }) => {
  const sColor = m.sentiment === "positive" ? "text-emerald-300 bg-emerald-500/10" : m.sentiment === "negative" ? "text-red-300 bg-red-500/10" : "text-gray-300 bg-white/5";
  const rColor = m.risk_level === "high" ? "text-red-300 border-red-500/30" : m.risk_level === "medium" ? "text-yellow-300 border-yellow-500/30" : "text-cyan-300 border-cyan-500/30";
  return (
    <div className="p-4 rounded-xl bg-[#0A0A0F] border border-white/[0.06] hover:border-purple-500/20 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <a href={m.link} target="_blank" rel="noreferrer" className="text-sm font-medium text-white hover:text-purple-300 transition-colors flex items-start gap-2">
            <span className="flex-1">{m.title}</span>
            <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0 opacity-50" />
          </a>
          {m.description && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{m.description}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
            <span className="text-gray-500">{m.source}</span>
            {m.published_iso && <span className="text-gray-600">· {new Date(m.published_iso).toLocaleDateString("es-SV")}</span>}
            <span className={`px-2 py-0.5 rounded ${sColor} uppercase tracking-widest font-medium`}>{m.sentiment} {m.sentiment_score}</span>
            <span className={`px-2 py-0.5 rounded border ${rColor} uppercase tracking-widest font-medium`}>{m.risk_level}</span>
            {m.ai_note && <span className="text-purple-300/70 italic flex items-center gap-1"><Cpu className="w-2.5 h-2.5" />{m.ai_note}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const BriefTab = ({ brief, onRegenerate }) => {
  if (brief === "LOADING") return <div className="p-12 rounded-2xl bg-[#0A0A0F] border border-white/[0.08] flex flex-col items-center gap-3"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /><p className="text-sm text-gray-400">Claude está analizando menciones reales y generando brief estratégico...</p></div>;
  if (!brief) return <div className="p-12 rounded-2xl bg-[#0A0A0F] border border-white/[0.08] text-center text-gray-400 text-sm">Click en "Brief Estratégico IA" para generar análisis con Claude basado en datos reales.</div>;
  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6" data-testid="brief-content">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Brief Estratégico generado por Claude</h3>
        <button onClick={onRegenerate} className="text-xs text-purple-300 hover:underline flex items-center gap-1"><RefreshCw className="w-3 h-3" />Regenerar</button>
      </div>
      <div className="prose prose-invert prose-sm max-w-none text-gray-300 whitespace-pre-wrap leading-relaxed">{brief}</div>
    </div>
  );
};

const StakeholdersTab = ({ data, onLoad }) => {
  if (!data) return <div className="p-12 rounded-2xl bg-[#0A0A0F] border border-white/[0.08] flex flex-col items-center gap-3"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /><p className="text-sm text-gray-400">Extrayendo stakeholders con IA...</p></div>;
  const ally = data.stakeholders?.filter(s => s.type === "ally") || [];
  const critic = data.stakeholders?.filter(s => s.type === "critic") || [];
  const other = data.stakeholders?.filter(s => !["ally", "critic"].includes(s.type)) || [];
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="stakeholders-map">
      <StakeholderColumn title="Aliados" emoji="↑" stakeholders={ally} color="emerald" />
      <StakeholderColumn title="Críticos" emoji="↓" stakeholders={critic} color="red" />
      <StakeholderColumn title="Otros / Institucionales" emoji="·" stakeholders={other} color="gray" />
    </div>
  );
};

const StakeholderColumn = ({ title, stakeholders, color }) => {
  const borderColor = color === "emerald" ? "border-emerald-500/20" : color === "red" ? "border-red-500/20" : "border-white/10";
  return (
    <div className={`rounded-2xl bg-[#0A0A0F] border ${borderColor} p-5`}>
      <h3 className="text-xs font-bold text-white mb-3 uppercase tracking-widest">{title} ({stakeholders.length})</h3>
      <div className="space-y-2">
        {stakeholders.map((s, i) => (
          <div key={`stk-${s.name || i}`} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
            <div className="flex items-start justify-between">
              <p className="text-sm font-medium text-white">{s.name}</p>
              <span className="text-[9px] uppercase tracking-widest text-gray-500">{s.influence}</span>
            </div>
            {s.note && <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{s.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const ForecastTab = ({ data, onLoad }) => {
  if (!data) return <div className="p-12 rounded-2xl bg-[#0A0A0F] border border-white/[0.08] flex flex-col items-center gap-3"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /><p className="text-sm text-gray-400">Calculando proyección...</p></div>;
  const w = 100, h = 60;
  const pts = data.forecast.map((f, i) => `${(i / (data.forecast.length - 1)) * w},${h - (f.score / 100) * h}`).join(" ");
  return (
    <div className="space-y-4" data-testid="forecast-content">
      <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" />Proyección Reputacional 90 días</h3>
        <svg viewBox={`-2 -4 ${w + 4} ${h + 8}`} className="w-full h-32 mb-4">
          <polyline points={pts} fill="none" stroke="url(#fGrad)" strokeWidth="1.2" strokeLinecap="round" />
          <defs><linearGradient id="fGrad" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#10b981" /><stop offset="1" stopColor="#a855f7" /></linearGradient></defs>
        </svg>
        <div className="grid grid-cols-3 gap-3">
          {data.scenarios.map(s => (
            <div key={s.name} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <div className="flex justify-between items-center mb-1">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest">{s.name}</p>
                <span className="text-[10px] text-white font-mono">{s.probability}%</span>
              </div>
              <p className="text-lg font-bold text-white">{s.iri_90d}</p>
              <p className="text-[10px] text-gray-500 leading-snug">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6">
        <h3 className="text-sm font-bold text-white mb-3">Drivers principales</h3>
        {data.key_drivers.map((d, i) => (
          <div key={`pol-drv-${d.driver || i}`} className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-300">{d.driver}</span>
            <div className="flex items-center gap-2">
              <div className="w-24 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{ width: `${d.weight * 2}%` }} />
              </div>
              <span className="text-xs text-gray-500 w-10 text-right">{d.weight}%</span>
              <span className={`text-[10px] uppercase ${d.trend === "positive" ? "text-emerald-400" : d.trend === "negative" ? "text-red-400" : "text-gray-500"}`}>{d.trend}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PoliticalIntel;
